import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdminClientePerfil, getColorHex } from '../../../hooks/useAdminClientePerfil';
import type { ClientePerfil } from '../../../hooks/useAdminClientePerfil';
import type { Prenda } from '../../../hooks/useClienteArmario';
import type { Recomendacion } from '../../../hooks/useClienteRecomendaciones';
import type { LookCalendario } from '../../../hooks/useClienteCalendario';
import { PerfilEstilisticoPDFDownload } from '../../../components/PerfilEstilisticoPDF';

interface PerfilEstilisticoProps {
  clienteId: number;
  prendas: Prenda[];
  recomendaciones: Recomendacion[];
  calendario: LookCalendario[];
  perfilOverride?: ClientePerfil;
  nombreCliente?: string;
}

function getTonoPielStyle(tono?: string | null) {
  const t = tono?.toLowerCase() ?? '';
  if (t.includes('cálido') || t.includes('calido') || t.includes('warm')) {
    return {
      cardBg: 'bg-amber-50',
      cardBorder: 'border-amber-200',
      badge: 'bg-amber-100 text-amber-800',
      desc: 'text-amber-700',
      icon: 'text-amber-600',
    };
  }
  if (t.includes('frío') || t.includes('frio') || t.includes('cool')) {
    return {
      cardBg: 'bg-sky-50',
      cardBorder: 'border-sky-200',
      badge: 'bg-sky-100 text-sky-800',
      desc: 'text-sky-700',
      icon: 'text-sky-600',
    };
  }
  if (t.includes('neutro') || t.includes('neutral')) {
    return {
      cardBg: 'bg-stone-50',
      cardBorder: 'border-stone-200',
      badge: 'bg-stone-100 text-stone-700',
      desc: 'text-stone-600',
      icon: 'text-stone-500',
    };
  }
  return {
    cardBg: 'bg-white',
    cardBorder: 'border-brand-gray-200',
    badge: 'bg-brand-black text-white',
    desc: 'text-brand-gray-500',
    icon: 'text-brand-black',
  };
}

export default function PerfilEstilistico({
  clienteId,
  prendas,
  recomendaciones,
  calendario,
  perfilOverride,
  nombreCliente = 'Cliente',
}: PerfilEstilisticoProps) {
  const { perfil: perfilDb, loading: loadingPerfil } = useAdminClientePerfil(clienteId);
  const perfil = perfilOverride ?? perfilDb;

  const stats = useMemo(() => {
    const totalPrendas = prendas.length;
    const prendasActivas = prendas.filter((p) => p.activa).length;
    const totalLooks = calendario.length;
    const looksUsados = calendario.filter((l) => l.estado === 'usado').length;
    const nuevasRec = recomendaciones.filter((r) => !r.leida).length;

    const colorMap: Record<string, number> = {};
    prendas.forEach((p) => {
      colorMap[p.color] = (colorMap[p.color] || 0) + 1;
    });
    const topColores = Object.entries(colorMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([c]) => c);

    const porCategoria: Record<string, number> = {};
    prendas.forEach((p) => {
      porCategoria[p.categoria] = (porCategoria[p.categoria] || 0) + 1;
    });
    const categoriasPrincipales = Object.entries(porCategoria)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([cat]) => cat);

    return {
      totalPrendas,
      prendasActivas,
      totalLooks,
      looksUsados,
      nuevasRec,
      topColores,
      categoriasPrincipales,
    };
  }, [prendas, calendario, recomendaciones]);

  const tienePerfilDefinido = !!(
    (perfil?.paleta_colores && perfil.paleta_colores.length > 0) ||
    perfil?.estilo_personal ||
    perfil?.tono_piel ||
    perfil?.notas_estilista ||
    (perfil?.ocasiones_preferidas && perfil.ocasiones_preferidas.length > 0) ||
    (perfil?.prendas_a_evitar && perfil.prendas_a_evitar.length > 0)
  );

  const tonoStyle = getTonoPielStyle(perfil?.tono_piel);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-brand-black text-3xl font-light">
            Mi Perfil Estilístico
          </h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            Todo lo que Anthony ha definido para ti
          </p>
        </div>
        {perfil && (
          <PerfilEstilisticoPDFDownload
            perfil={perfil}
            nombreCliente={nombreCliente}
          >
            {({ loading }) => (
              <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-brand-gray-200 font-sans text-sm text-brand-black hover:bg-brand-black hover:text-white transition-colors whitespace-nowrap shrink-0">
                {loading ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-file-download-line" />
                )}
                {loading ? 'Generando PDF...' : 'Descargar PDF'}
              </button>
            )}
          </PerfilEstilisticoPDFDownload>
        )}
      </div>

      {/* Si no hay perfil definido aún */}
      {!tienePerfilDefinido && !loadingPerfil && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mb-4">
            <i className="ri-user-star-line text-2xl" />
          </div>
          <p className="font-sans text-brand-gray-500 text-sm">
            Anthony está preparando tu perfil estilístico personalizado.
          </p>
          <p className="font-sans text-brand-gray-400 text-xs mt-1">
            Pronto verás aquí tu paleta de colores, estilo recomendado y notas personalizadas.
          </p>
        </div>
      )}

      {/* Paleta de colores recomendada */}
      {perfil?.paleta_colores && perfil.paleta_colores.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
              <i className="ri-palette-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-xl font-medium">
                Paleta de colores recomendada
              </h3>
              <p className="font-sans text-brand-gray-400 text-xs mt-0.5">
                Definido por Anthony Sánchez
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {perfil.paleta_colores.map((color) => {
              const hex = getColorHex(color);
              return (
                <div
                  key={color}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border border-brand-gray-200"
                  style={{ backgroundColor: hex + '14' }}
                >
                  <span
                    className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
                    style={{ backgroundColor: hex }}
                    title={color}
                  />
                  <span className="font-sans text-xs font-medium text-center text-brand-black">
                    {color}
                  </span>
                </div>
              );
            })}
          </div>

          <p className="font-sans text-brand-gray-500 text-sm mt-5 leading-relaxed">
            Estos son los colores que Anthony ha seleccionado especialmente para ti. Úsalos como guía
            al crear looks, comprar nuevas prendas o elegir accesorios. Los colores de tu armario
            que coincidan con esta paleta son los que mejor funcionan con tu tono de piel y estilo personal.
          </p>
        </div>
      )}

      {/* Estilo personal + Tono de piel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {perfil?.estilo_personal && (
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
                <i className="ri-sparkling-line text-lg" />
              </div>
              <div>
                <h3 className="font-serif text-brand-black text-lg font-medium">Mi estilo personal</h3>
                <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Definido por Anthony</p>
              </div>
            </div>
            <p className="font-sans text-brand-gray-700 text-sm leading-relaxed whitespace-pre-line">
              {perfil.estilo_personal}
            </p>
          </div>
        )}

        {perfil?.tono_piel && (
          <div className={`${tonoStyle.cardBg} border ${tonoStyle.cardBorder} rounded-2xl p-6 transition-colors`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full ${tonoStyle.cardBg} border ${tonoStyle.cardBorder} flex items-center justify-center ${tonoStyle.icon}`}>
                <i className="ri-sun-line text-lg" />
              </div>
              <div>
                <h3 className="font-serif text-brand-black text-lg font-medium">Tono de piel</h3>
                <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Definido por Anthony</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans text-sm font-medium ${tonoStyle.badge}`}>
                <i className="ri-user-smile-line" />
                {perfil.tono_piel}
              </span>
            </div>
            <p className={`font-sans text-xs mt-3 ${tonoStyle.desc}`}>
              {perfil.tono_piel.toLowerCase().includes('cálido') || perfil.tono_piel.toLowerCase().includes('calido')
                ? 'Tu tono de piel cálido se beneficia de colores dorados, terracotas, verdes bosque y marfil. Evita los plateados puros.'
                : perfil.tono_piel.toLowerCase().includes('frío') || perfil.tono_piel.toLowerCase().includes('frio')
                ? 'Tu tono de piel frío luce mejor con colores plateados, azules, rosas pálidos y grises perla. Evita los dorados intensos.'
                : perfil.tono_piel.toLowerCase().includes('neutro')
                ? 'Tu tono de piel neutro es versátil: tanto colores cálidos como fríos te favorecen. Experimenta con ambas familias.'
                : 'El tono de piel determina qué colores te favorecen más. Anthony ha evaluado el tuyo para crear una paleta personalizada.'}
            </p>
          </div>
        )}
      </div>

      {/* Notas del estilista */}
      {perfil?.notas_estilista && (
        <div className="bg-brand-cream border border-brand-gray-200 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center text-white">
              <i className="ri-quill-pen-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-lg font-medium">Notas de Anthony</h3>
              <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Para ti</p>
            </div>
          </div>
          <blockquote className="relative pl-5 border-l-2 border-brand-black">
            <p className="font-sans text-brand-gray-700 text-sm leading-relaxed italic whitespace-pre-line">
              {perfil.notas_estilista}
            </p>
          </blockquote>
        </div>
      )}

      {/* Ocasiones preferidas */}
      {perfil?.ocasiones_preferidas && perfil.ocasiones_preferidas.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
              <i className="ri-calendar-check-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-lg font-medium">Ocasiones que más te importan</h3>
              <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Definido por Anthony</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {perfil.ocasiones_preferidas.map((ocasion) => (
              <span
                key={ocasion}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-cream font-sans text-sm text-brand-black border border-brand-gray-200"
              >
                <i className="ri-calendar-event-line text-brand-gray-400 text-xs" />
                {ocasion}
              </span>
            ))}
          </div>
          <p className="font-sans text-brand-gray-500 text-xs mt-3">
            Anthony ha definido estas ocasiones como las prioritarias en tu asesoría. Los looks y recomendaciones se enfocarán en cubrir estos momentos.
          </p>
        </div>
      )}

      {/* Prendas a evitar */}
      {perfil?.prendas_a_evitar && perfil.prendas_a_evitar.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500">
              <i className="ri-forbid-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-lg font-medium">Prendas a evitar</h3>
              <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Definido por Anthony</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {perfil.prendas_a_evitar.map((prenda) => (
              <span
                key={prenda}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-50 font-sans text-sm text-red-700 border border-red-200"
              >
                <i className="ri-close-circle-line text-red-400 text-xs" />
                {prenda}
              </span>
            ))}
          </div>
          <p className="font-sans text-brand-gray-500 text-xs mt-3">
            Estos tipos de prenda no favorecen tu silueta o no se alinean con tu estilo personal. Evítalos al comprar o al elegir looks.
          </p>
        </div>
      )}

      {/* Combinaciones personalizadas por Anthony */}
      {perfil?.combinaciones_personalizadas && perfil.combinaciones_personalizadas.length > 0 && (
        <div className="bg-brand-cream border border-brand-gray-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-brand-black flex items-center justify-center text-white">
              <i className="ri-shuffle-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-lg font-medium">Combinaciones personalizadas</h3>
              <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Definido por Anthony</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {perfil.combinaciones_personalizadas.map((combo, idx) => (
              <div key={idx} className="bg-white border border-brand-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex -space-x-2">
                    <span
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: getColorHex(combo.base) }}
                    />
                    <span
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: getColorHex(combo.complemento) }}
                    />
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium text-brand-black">{combo.base} + {combo.complemento}</p>
                    {combo.tono && <span className="font-sans text-[11px] text-brand-gray-400">{combo.tono}</span>}
                  </div>
                </div>
                {combo.desc && <p className="font-sans text-xs text-brand-gray-500">{combo.desc}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Combinaciones automáticas basadas en paleta */}
      {perfil?.paleta_colores && perfil.paleta_colores.length >= 2 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
              <i className="ri-shuffle-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-lg font-medium">Combinaciones automáticas</h3>
              <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Basado en tu paleta</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Generar combinaciones dinámicas */}
            {(() => {
              const colors = perfil.paleta_colores ?? [];
              const neutral = ['Blanco', 'Negro', 'Gris perla', 'Beige', 'Crudo', 'Oro', 'Plata', 'Cobre'];
              const combos: { base: string; comp: string; desc: string }[] = [];
              for (let i = 0; i < colors.length; i++) {
                for (let j = i + 1; j < colors.length; j++) {
                  const a = colors[i];
                  const b = colors[j];
                  if (neutral.includes(a) || neutral.includes(b)) {
                    const base = neutral.includes(a) ? b : a;
                    const comp = neutral.includes(a) ? a : b;
                    combos.push({
                      base,
                      comp,
                      desc: `Base ${base.toLowerCase()} con acento ${comp.toLowerCase()} — versátil y elegante.`,
                    });
                  }
                }
              }
              return combos.slice(0, 4).map((combo, idx) => (
                <div key={idx} className="bg-brand-cream border border-brand-gray-200 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex -space-x-2">
                      <span
                        className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: getColorHex(combo.base) }}
                      />
                      <span
                        className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                        style={{ backgroundColor: getColorHex(combo.comp) }}
                      />
                    </div>
                    <div>
                      <p className="font-sans text-sm font-medium text-brand-black">{combo.base} + {combo.comp}</p>
                    </div>
                  </div>
                  <p className="font-sans text-xs text-brand-gray-500">{combo.desc}</p>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Looks sugeridos combinando prendas reales del armario */}
      {perfil?.paleta_colores && perfil.paleta_colores.length >= 2 && prendas.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
              <i className="ri-shirt-line text-lg" />
            </div>
            <div>
              <h3 className="font-serif text-brand-black text-xl font-medium">Looks sugeridos</h3>
              <p className="font-sans text-brand-gray-400 text-xs mt-0.5">
                Combinaciones con prendas de tu armario
              </p>
            </div>
          </div>

          {(() => {
            const paleta = (perfil.paleta_colores ?? []).map((c) => c.toLowerCase());
            const prendasActivas = prendas.filter((p) => p.activa);
            const prendasPaleta = prendasActivas.filter((p) =>
              paleta.some((col) => p.color.toLowerCase().includes(col))
            );
            const tops = prendasPaleta.filter((p) =>
              ['Camiseta', 'Camisa', 'Blusa', 'Suéter', 'Jersey', 'Blazer'].some((t) =>
                p.categoria.toLowerCase().includes(t.toLowerCase())
              )
            );
            const bottoms = prendasPaleta.filter((p) =>
              ['Pantalón', 'Pantalon', 'Falda', 'Vestido', 'Short'].some((t) =>
                p.categoria.toLowerCase().includes(t.toLowerCase())
              )
            );
            const outerwear = prendasPaleta.filter((p) =>
              ['Chaqueta', 'Abrigo', 'Cardigan', 'Capa'].some((t) =>
                p.categoria.toLowerCase().includes(t.toLowerCase())
              )
            );

            const looks: { nombre: string; prendas: { nombre: string; color: string; categoria: string }[]; ocasion: string }[] = [];
            const ocasiones = perfil.ocasiones_preferidas ?? ['Diario'];
            const randomOcasion = (idx: number) => ocasiones[idx % ocasiones.length];

            for (let i = 0; i < Math.min(3, tops.length); i++) {
              const top = tops[i];
              const bottom = bottoms[i % bottoms.length] ?? bottoms[0];
              const layer = outerwear[i % outerwear.length] ?? null;
              if (top && bottom) {
                const prendaList = [
                  { nombre: top.nombre, color: top.color, categoria: top.categoria },
                  { nombre: bottom.nombre, color: bottom.color, categoria: bottom.categoria },
                ];
                if (layer) prendaList.push({ nombre: layer.nombre, color: layer.color, categoria: layer.categoria });
                looks.push({
                  nombre: `Look ${i + 1}`,
                  prendas: prendaList,
                  ocasion: randomOcasion(i),
                });
              }
            }

            if (looks.length === 0) {
              return (
                <div className="text-center py-8 text-brand-gray-400">
                  <i className="ri-t-shirt-line text-2xl mb-2" />
                  <p className="font-sans text-sm">
                    Añade más prendas a tu armario que coincidan con la paleta para ver looks sugeridos.
                  </p>
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {looks.map((look) => (
                  <div key={look.nombre} className="bg-brand-cream border border-brand-gray-200 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-serif text-brand-black text-base font-medium">{look.nombre}</span>
                      <span className="font-sans text-[10px] px-2 py-1 rounded-full bg-brand-sage/20 text-brand-black">
                        {look.ocasion}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {look.prendas.map((p) => (
                        <div key={p.nombre} className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                            style={{ backgroundColor: getColorHex(p.color) }}
                          />
                          <p className="font-sans text-sm text-brand-gray-700">
                            {p.categoria}: {p.nombre}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Medidas corporales */}
      {perfil?.medidas_corporales && (
        (perfil.medidas_corporales.altura ||
          perfil.medidas_corporales.talla ||
          perfil.medidas_corporales.contorno_pecho ||
          perfil.medidas_corporales.contorno_cintura ||
          perfil.medidas_corporales.contorno_cadera ||
          perfil.medidas_corporales.contorno_brazo ||
          perfil.medidas_corporales.largo_pierna) && (
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
                <i className="ri-ruler-line text-lg" />
              </div>
              <div>
                <h3 className="font-serif text-brand-black text-lg font-medium">Mis medidas</h3>
                <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Para ajuste perfecto</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { key: 'altura' as const, label: 'Altura' },
                { key: 'talla' as const, label: 'Talla habitual' },
                { key: 'contorno_pecho' as const, label: 'Pecho' },
                { key: 'contorno_cintura' as const, label: 'Cintura' },
                { key: 'contorno_cadera' as const, label: 'Cadera' },
                { key: 'contorno_brazo' as const, label: 'Brazo' },
                { key: 'largo_pierna' as const, label: 'Largo pierna' },
              ]
                .filter((f) => !!perfil.medidas_corporales?.[f.key])
                .map((field) => (
                  <div key={field.key} className="bg-brand-cream rounded-xl p-4 border border-brand-gray-200">
                    <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">
                      {field.label}
                    </p>
                    <p className="font-serif text-brand-black text-lg font-medium mt-1">
                      {perfil.medidas_corporales![field.key]}
                    </p>
                  </div>
                ))}
            </div>

            {perfil.medidas_corporales.notas_medidas && (
              <div className="mt-4">
                <p className="font-sans text-brand-gray-500 text-xs italic">
                  {perfil.medidas_corporales.notas_medidas}
                </p>
              </div>
            )}
          </div>
        )
      )}

      {/* Tallas por marca */}
      {perfil?.medidas_corporales?.tallas_por_marca &&
        Object.keys(perfil.medidas_corporales.tallas_por_marca).length > 0 && (
          <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-brand-sage/20 flex items-center justify-center text-brand-black">
                <i className="ri-shopping-bag-3-line text-lg" />
              </div>
              <div>
                <h3 className="font-serif text-brand-black text-lg font-medium">Tallas por marca</h3>
                <p className="font-sans text-brand-gray-400 text-[10px] uppercase tracking-wider">Para compras online</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(perfil.medidas_corporales.tallas_por_marca).map(([marca, talla]) => (
                <div key={marca} className="flex items-center gap-3 bg-brand-cream rounded-xl p-4 border border-brand-gray-200">
                  <div className="w-8 h-8 rounded-full bg-brand-black text-white flex items-center justify-center font-sans text-xs font-bold shrink-0">
                    {marca.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-sans text-brand-black text-sm font-medium truncate">{marca}</p>
                    <p className="font-sans text-brand-sage text-sm font-medium">{talla}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="font-sans text-brand-gray-500 text-xs mt-3">
              Anthony ha registrado tus tallas por marca para que puedas comprar online con confianza.
            </p>
          </div>
        )}

      {/* Resumen de datos del armario */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-t-shirt-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">{stats.totalPrendas}</p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">Prendas registradas</p>
        </div>
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-calendar-event-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">{stats.totalLooks}</p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">Looks planificados</p>
        </div>
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-lightbulb-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">{stats.nuevasRec}</p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">Recomendaciones nuevas</p>
        </div>
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-5">
          <div className="w-8 h-8 flex items-center justify-center text-brand-sage mb-3">
            <i className="ri-checkbox-circle-line text-xl" />
          </div>
          <p className="font-serif text-brand-black text-2xl font-light">{stats.looksUsados}</p>
          <p className="font-sans text-brand-gray-500 text-xs mt-1">Looks ya usados</p>
        </div>
      </div>

      {/* Categorías principales */}
      {stats.categoriasPrincipales.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
          <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium mb-4">
            Categorías principales de tu armario
          </h3>
          <div className="flex flex-wrap gap-2">
            {stats.categoriasPrincipales.map((cat) => (
              <span
                key={cat}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-cream font-sans text-sm text-brand-black border border-brand-gray-200"
              >
                <i className="ri-t-shirt-line text-brand-gray-400 text-xs" />
                {cat}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Colores predominantes del armario */}
      {stats.topColores.length > 0 && (
        <div className="bg-white border border-brand-gray-200 rounded-2xl p-6">
          <h3 className="font-sans text-brand-gray-500 text-[11px] tracking-wider uppercase font-medium mb-4">
            Colores que más usas
          </h3>
          <div className="flex flex-wrap gap-3">
            {stats.topColores.map((color) => {
              const hex = getColorHex(color);
              return (
                <span
                  key={color}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gray-200 font-sans text-xs"
                  style={{ backgroundColor: hex + '20' }}
                >
                  <span
                    className="w-3 h-3 rounded-full border border-brand-gray-300/50 shrink-0"
                    style={{ backgroundColor: hex }}
                  />
                  {color}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Navegación rápida */}
      <div className="bg-brand-black rounded-2xl p-6 md:p-8 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl font-medium">¿Quieres ver más?</h3>
            <p className="font-sans text-white/70 text-sm mt-1">
              Explora tu armario, calendario y recomendaciones de Anthony.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/mi-asesoria"
              onClick={() => {
                const navEvent = new CustomEvent('navigate-backoffice', { detail: 'armario' });
                window.dispatchEvent(navEvent);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors font-sans text-sm whitespace-nowrap"
            >
              <i className="ri-t-shirt-line" />
              Mi Armario
            </Link>
            <Link
              to="/mi-asesoria"
              onClick={() => {
                const navEvent = new CustomEvent('navigate-backoffice', { detail: 'calendario' });
                window.dispatchEvent(navEvent);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors font-sans text-sm whitespace-nowrap"
            >
              <i className="ri-calendar-event-line" />
              Calendario
            </Link>
            <Link
              to="/mi-asesoria"
              onClick={() => {
                const navEvent = new CustomEvent('navigate-backoffice', { detail: 'combinaciones' });
                window.dispatchEvent(navEvent);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors font-sans text-sm whitespace-nowrap"
            >
              <i className="ri-magic-line" />
              Combinaciones
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}